import type { FC } from "react";
import { useState } from "react";

import { useLocationContext } from "@/src/contexts/LocationContext";
import type { IComponent } from "@Components/Component";
import { defaultAttributes } from "@Components/Component";
import usePageInitialScroll from "@Components/Hooks/usePageInitialScroll";
import type { IMap } from "@Components/Map";
import { Map, MapMarker } from "@Components/Map";
import { jumpToSectionElement } from "@Components/Modules/Helpers";
import type { IPanel } from "@Components/Panel";
import { Panel } from "@Components/Panel";

import type { IShoppingMallMapMarker } from "../map/ShoppingMallMapMarker";
import { ShoppingMallMapMarkerInfo } from "../map/ShoppingMallMapMarker";
import type { IShoppingMallsAllPanel } from "./ShoppingMallsAllPanel";
import { ShoppingMallsAllPanel } from "./ShoppingMallsAllPanel";
import type { IShoppingMallsObserved, IShoppingMallsObservedResult } from "./ShoppingMallsObserved";
import { ShoppingMallsObserved } from "./ShoppingMallsObserved";

import * as css from "./ShoppingMallsTab.module.css";

export interface IShoppingMallsListsMap extends IComponent {
    observedShoppingMalls: IShoppingMallsObserved;
    allShoppingMallsInArea: IShoppingMallsAllPanel;
    shoppingMallsMapPanel: IPanel;
    mapComponent: IMap;
    isLazyLoading?: boolean;
}

const ShoppingMallsListsMap: FC<IShoppingMallsListsMap> = (props) => {
    const { location } = useLocationContext();
    const popularShoppingMallsRef = usePageInitialScroll<HTMLDivElement>(
        "position",
        props.observedShoppingMalls.clientId,
    );
    const nearbyShoppingMallsRef = usePageInitialScroll<HTMLDivElement>(
        "position",
        props.shoppingMallsMapPanel.clientId,
    );
    const [selectedMarker, setSelectedMarker] = useState<IShoppingMallMapMarker | null>(null);
    const [observedPanel, setObservedPanel] = useState<IShoppingMallsObserved | null>(
        props.observedShoppingMalls,
    );

    const mapPanel = (): JSX.Element | null => {
        if (!props.mapComponent) {
            return null;
        }

        const marker = (markerProps: IShoppingMallMapMarker) => {
            const markerInfo = (markerInfoProps: IShoppingMallMapMarker) => (
                <ShoppingMallMapMarkerInfo {...markerInfoProps.info} />
            );

            const isOpen: { showInfo?: boolean } = {};
            if (selectedMarker) {
                isOpen.showInfo = markerProps.id === selectedMarker.id;
            }

            const onOpen = () => {
                if (selectedMarker && selectedMarker.id !== markerProps.id) {
                    setSelectedMarker(null);
                }

                if (markerProps.onOpenInfo) {
                    markerProps.onOpenInfo();
                }
            };

            return (
                <MapMarker
                    {...markerProps}
                    renderInfoContent={markerInfo}
                    {...isOpen}
                    onOpenInfo={onOpen}
                    key={markerProps.id}
                />
            );
        };

        let { defaultPosition } = props.mapComponent;
        if (location.latitude !== null && location.longitude !== null) {
            defaultPosition = {
                lat: location.latitude,
                lng: location.longitude,
            };
        }

        const mapOptions: google.maps.MapOptions = selectedMarker
            ? { ...Map.DefaultMapOptions, center: selectedMarker.position, zoom: 15 }
            : { ...Map.DefaultMapOptions, center: undefined };

        return (
            <Panel
                {...props.shoppingMallsMapPanel}
                ref={nearbyShoppingMallsRef}
                titleElement="h2"
                className={css.shoppingMallsMapPanel}
            >
                <Map
                    mapOptions={mapOptions}
                    {...props.mapComponent}
                    defaultPosition={defaultPosition}
                    renderMarker={marker}
                />
            </Panel>
        );
    };

    const observedLoaded = (response: IShoppingMallsObservedResult) => {
        if (!response || !response.items || response.items.length === 0) {
            setObservedPanel(null);
        }
    };

    const showLocationClick = (_event: React.MouseEvent<HTMLElement>, marker: IShoppingMallMapMarker) => {
        setSelectedMarker(marker);

        if (!nearbyShoppingMallsRef.current) {
            return;
        }

        jumpToSectionElement(nearbyShoppingMallsRef.current);
    };

    const observed = observedPanel && {
        ...observedPanel,
        ajaxSuccess: [observedLoaded],
        onMapMarkerClick: showLocationClick,
    };

    const allShoppingsPanel = {
        ...props.allShoppingMallsInArea,
        placeDescription: location.placeDescription,
        onMapMarkerClick: showLocationClick,
    };

    return (
        <div {...defaultAttributes(props, css.shoppingMallsListsMapComponent)}>
            <ShoppingMallsObserved {...observed} panelRef={popularShoppingMallsRef} />
            <ShoppingMallsAllPanel {...allShoppingsPanel} />
            {mapPanel()}
        </div>
    );
};

export default ShoppingMallsListsMap;
